import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { MediaDisplay } from '../components/MediaDisplay';
import { EmptyState } from '../components/EmptyState';

export const BlueskyPreview = ({ caption, media, channel }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.bluesky;
  const displayCaption = truncateForPlatform(caption, 'bluesky');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';
  const handle = channel?.handle ?? '';

  if (isEmpty) return <EmptyState />;

  // For 2-4 images, show 2x2 grid. For single image, show normal. For carousel (more than 4), use carousel.
  const displayMedia = media.slice(0, 4);
  const shouldUseGrid = displayMedia.length >= 2 && displayMedia.length <= 4;

  return (
    <div className="bg-white w-full min-w-0 max-w-[600px] mx-auto border border-gray-200 rounded-lg">
      <div className="p-4 flex gap-3">
        {/* Avatar */}
        <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1 flex-wrap">
            <span className="max-w-full truncate font-bold text-gray-900 text-sm">{displayName}</span>
            <span className="max-w-full truncate text-gray-500 text-sm">{handle}</span>
            <span className="text-gray-400 text-sm">- {t('preview_now')}</span>
          </div>

          {/* Content */}
          {caption && (
            <PreviewCaption
              caption={displayCaption}
              hashtagColor={config.hashtagColor}
              className="text-gray-900 text-sm mt-1"
              truncated={caption.length > 150}
            />
          )}

          {/* Media */}
          {media.length > 0 && (
            <div className="mt-3 rounded-lg overflow-hidden">
              {shouldUseGrid ? (
                <div className="grid grid-cols-2 gap-0.5">
                  {displayMedia.map((item, idx) => (
                    <div key={item.id || idx} className="aspect-square">
                      <img
                        src={item.url}
                        alt={t('preview_media_alt', { index: idx + 1 })}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <MediaDisplay
                  media={media.slice(0, 1)}
                  aspectRatio="auto"
                  rounded="lg"
                  showCarouselDots={false}
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 text-gray-500 max-w-sm">
            <button
              type="button"
              className="flex items-center gap-1 hover:text-[#0085FF] transition-colors"
            >
              <Icon name={ICONS.CHAT} size="sm" />
            </button>
            <button
              type="button"
              className="flex items-center gap-1 hover:text-green-500 transition-colors"
            >
              <Icon name={ICONS.REFRESH} size="sm" />
            </button>
            <button
              type="button"
              className="flex items-center gap-1 hover:text-red-500 transition-colors"
            >
              <Icon name={ICONS.HEART} size="sm" />
            </button>
            <button type="button" className="hover:text-[#0085FF] transition-colors">
              <Icon name={ICONS.SHARE} size="sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
